import { inject, Injectable } from '@angular/core';
import { ApiDataAccess } from '@softline/core';
import {
  Workflow,
  WorkflowAction,
  WorkflowComment,
  WorkflowCommentRequest,
  WorkflowHistory,
  WorkflowParticipants,
} from '../types/workflow';
import {
  SOFTLINE_API_WORKFLOW_BY_ID,
  SOFTLINE_API_WORKFLOW_COMMENT_BY_ID,
  SOFTLINE_API_WORKFLOW_COMMENTS,
  SOFTLINE_API_WORKFLOW_CREATE,
  SOFTLINE_API_WORKFLOW_HISTORY,
  SOFTLINE_API_WORKFLOW_NEXT_ACTION,
  SOFTLINE_API_WORKFLOW_PARTICIPANTS,
  SOFTLINE_API_WORKFLOWS,
  SOFTLINE_API_WORKFLOWS_ALL,
  SOFTLINE_API_WORKFLOWS_QUERY,
} from '../workflow.api';
import { WorkflowTypeType } from '../types/workflow-type';

@Injectable()
export class WorkflowRepository {
  private readonly dataAccess = inject(ApiDataAccess);

  loadById(id: number): Promise<Workflow> {
    return this.dataAccess.get<Workflow>({
      path: SOFTLINE_API_WORKFLOW_BY_ID,
      pathParams: { id },
    });
  }

  loadAll(): Promise<Workflow[]> {
    return this.dataAccess.get<Workflow[]>({
      path: SOFTLINE_API_WORKFLOWS_ALL,
    });
  }

  queryWorkflows(type: string): Promise<Workflow[]> {
    return this.dataAccess.get<Workflow[]>({
      path: SOFTLINE_API_WORKFLOWS_QUERY,
      pathParams: { type },
    });
  }

  loadHistory(workflowId: number): Promise<WorkflowHistory[]> {
    return this.dataAccess.get<WorkflowHistory[]>({
      path: SOFTLINE_API_WORKFLOW_HISTORY,
      pathParams: { workflowId },
    });
  }

  loadComments(workflowId: number): Promise<WorkflowComment[]> {
    return this.dataAccess.get<WorkflowComment[]>({
      path: SOFTLINE_API_WORKFLOW_COMMENTS,
      pathParams: { workflowId },
    });
  }

  createWorkflow(type: WorkflowTypeType): Promise<Workflow> {
    return this.dataAccess.create<{ type: WorkflowTypeType }, Workflow>(
      { path: SOFTLINE_API_WORKFLOW_CREATE },
      { type: type }
    );
  }

  createComment(
    body: Partial<WorkflowCommentRequest>,
    params: { workflowId: number }
  ): Promise<WorkflowComment> {
    return this.dataAccess.create<Partial<WorkflowCommentRequest>, WorkflowComment>(
      {
        path: SOFTLINE_API_WORKFLOW_COMMENTS,
        pathParams: { workflowId: params.workflowId },
      },
      body
    );
  }

  createNextAction(body: WorkflowAction, params: { id: number }): Promise<Workflow> {
    return this.dataAccess.create<WorkflowAction, Workflow>(
      { path: SOFTLINE_API_WORKFLOW_NEXT_ACTION, pathParams: { id: params?.id } },
      body
    );
  }

  patch(body: Partial<Workflow>, params: { id: number }): Promise<Workflow[]> {
    return this.dataAccess.patch<Partial<Workflow>, Workflow[]>(
      {
        path: SOFTLINE_API_WORKFLOWS,
        pathParams: { id: params?.id },
      },
      body
    );
  }

  updateParticipants(
    body: WorkflowParticipants,
    params: { id: number }
  ): Promise<Workflow> {
    return this.dataAccess.create<WorkflowParticipants, Workflow>(
      { path: SOFTLINE_API_WORKFLOW_PARTICIPANTS, pathParams: { id: params.id } },
      body
    );
  }

  delete(params: { id: number }): Promise<Workflow[]> {
    return this.dataAccess.delete<Workflow[]>({
      path: SOFTLINE_API_WORKFLOWS,
      pathParams: { id: params?.id },
    });
  }

  deleteComment(params: {
    workflowId: number;
    commentId: number | string;
  }): Promise<void> {
    return this.dataAccess.delete<void>({
      path: SOFTLINE_API_WORKFLOW_COMMENT_BY_ID,
      pathParams: { workflowId: params.workflowId, commentId: params.commentId },
    });
  }
}
